import { useState, useMemo, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { 
  X, ChevronLeft, ChevronRight, Settings2, 
  Download, Building2, Palette, ChevronDown,
  Loader2
} from 'lucide-react';
import html2canvas from 'html2canvas';
import { useToast } from '@/hooks/use-toast';
import { getStageGroup } from '@/lib/chargingStages';
import { cn } from '@/lib/utils';
import gvccLogo from '@/assets/gvcc-logo.png';
import { useMapWithProvider } from '@/hooks/useMapWithProvider';
import { useMapProvider } from '@/hooks/useMapProvider';

const STAGES = [
  { id: 'acquisitie', label: 'Acquisitie', color: '#0091ff' },
  { id: 'realisatie', label: 'Realisatie', color: '#ff9100' },
  { id: 'operatie', label: 'Operatie', color: '#28a745' },
];

export default function ChargingPresentatie() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const mapRef = useRef<HTMLDivElement>(null);

  // UI state
  const [filtersPanelOpen, setFiltersPanelOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  
  // Map provider from global settings (only admin can change)
  const { mapProvider, isAdmin } = useMapProvider();
  const { MapComponent, mapboxToken } = useMapWithProvider();
  
  // Filters state
  const [visibleLocationIds, setVisibleLocationIds] = useState<Set<string>>(new Set());
  const [markerSize, setMarkerSize] = useState(25);
  
  // Subcategory filters
  const [selectedCompetitorOrgs, setSelectedCompetitorOrgs] = useState<Set<string>>(new Set());
  const [selectedWishOrgs, setSelectedWishOrgs] = useState<Set<string>>(new Set());
  const [selectedCarrierOrgs, setSelectedCarrierOrgs] = useState<Set<string>>(new Set());
  const [showDCs, setShowDCs] = useState(false);
  const [showStandplaatsen, setShowStandplaatsen] = useState(false);
  const [carrierSelectionInitialized, setCarrierSelectionInitialized] = useState(false);
  
  // Branding state
  const [selectedBranding, setSelectedBranding] = useState<'greenvision' | 'custom'>('greenvision');
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null);
  
  // Collapsible states
  const [competitorExpanded, setCompetitorExpanded] = useState(false);
  const [wishExpanded, setWishExpanded] = useState(false);
  const [carrierExpanded, setCarrierExpanded] = useState(false);
  const [brandingExpanded, setBrandingExpanded] = useState(false);


  // Fetch locations
  const { data: locations = [] } = useQuery({
    queryKey: ['charging-locations-presentation'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('charging_locations')
        .select('*')
        .is('deleted_at', null)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data || [];
    },
  });

  // Fetch opportunity organizations (only these appear in branding)
  const { data: opportunityOrgs = [] } = useQuery({
    queryKey: ['opportunity-organizations'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('charging_opportunities')
        .select('org:organizations(id, name, logo_url, favicon_url, accent_color)')
        .not('org_id', 'is', null);
      if (error) throw error;
      
      // Deduplicate organizations
      const orgsMap = new Map();
      data?.forEach((item: any) => {
        if (item.org && !orgsMap.has(item.org.id)) {
          orgsMap.set(item.org.id, item.org);
        }
      });
      return Array.from(orgsMap.values()).sort((a, b) => a.name.localeCompare(b.name));
    },
  });

  // Fetch competitor organizations
  const { data: competitorOrgs = [] } = useQuery({
    queryKey: ['competitor-orgs-presentation'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('competitor_organisations')
        .select('id, name, accent_color, logo_url')
        .order('name');
      if (error) throw error;
      return data || [];
    },
  });

  // Fetch competitor sites
  const { data: competitorSites = [] } = useQuery({
    queryKey: ['competitor-sites-presentation'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('competitor_sites')
        .select('*, organisation:competitor_organisations(id, name, accent_color)')
        .order('name');
      if (error) throw error;
      return data || [];
    },
    enabled: selectedCompetitorOrgs.size > 0,
  });

  // Fetch wish organizations
  const { data: wishOrgs = [] } = useQuery({
    queryKey: ['wish-orgs-presentation'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('wish_organisations')
        .select('id, name, accent_color, logo_url')
        .order('name');
      if (error) throw error;
      return data || [];
    },
  });

  // Fetch wish sites
  const { data: wishSites = [] } = useQuery({
    queryKey: ['wish-sites-presentation'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('wish_sites')
        .select('*, organisation:wish_organisations(id, name, accent_color)')
        .order('name');
      if (error) throw error;
      return data || [];
    },
    enabled: selectedWishOrgs.size > 0,
  });

  // Fetch opportunity addresses with organization info
  const { data: opportunityAddresses = [] } = useQuery({
    queryKey: ['opportunity-addresses-presentation'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('opportunity_addresses')
        .select(`
          *, 
          opportunity:charging_opportunities(
            name, 
            org_id,
            organization:organizations(id, name, logo_url, favicon_url, website)
          )
        `)
        .not('latitude', 'is', null);
      if (error) throw error;
      return data || [];
    },
    enabled: showDCs || showStandplaatsen,
  });

  // Initialize visible locations
  useEffect(() => {
    if (locations.length > 0 && visibleLocationIds.size === 0) {
      const defaultVisible = locations
        .filter((loc: any) => getStageGroup(loc.stage) !== 'verloren')
        .map((loc: any) => loc.id);
      setVisibleLocationIds(new Set(defaultVisible));
    }
  }, [locations]);

  // Get current branding
  const currentBranding = useMemo(() => {
    if (selectedBranding === 'greenvision' || !selectedOrgId) {
      return {
        id: 'greenvision',
        name: 'GreenVision Charging',
        accentColor: '#22c55e',
        logo: null,
      };
    }
    const org = opportunityOrgs.find((o: any) => o.id === selectedOrgId);
    if (org) {
      return {
        id: org.id,
        name: org.name,
        accentColor: org.accent_color || '#22c55e',
        logo: org.logo_url || org.favicon_url,
      };
    }
    return {
      id: 'greenvision',
      name: 'GreenVision Charging',
      accentColor: '#22c55e',
      logo: null,
    };
  }, [selectedBranding, selectedOrgId, opportunityOrgs]);

  // Build map sites
  const mapSites = useMemo(() => {
    const chargingLocations = locations
      .filter((loc: any) => visibleLocationIds.has(loc.id) && loc.latitude && loc.longitude)
      .map((loc: any) => ({
        id: loc.id,
        name: loc.name,
        address: loc.address || loc.city,
        city: loc.city,
        latitude: loc.latitude,
        longitude: loc.longitude,
        type: loc.stage,
        site_type: loc.stage,
        category: 'charging',
        displayOptions: { showName: true, showCity: true },
      }));

    // Filter competitor sites by selected organizations
    const competitors = competitorSites
      .filter((site: any) => 
        site.latitude && 
        site.longitude && 
        selectedCompetitorOrgs.has(site.organisation_id)
      )
      .map((site: any) => ({
        id: `competitor-${site.id}`,
        name: site.name,
        address: site.city,
        city: site.city,
        latitude: site.latitude,
        longitude: site.longitude,
        type: 'competitor',
        site_type: 'competitor',
        category: 'competitor',
        orgId: site.organisation_id,
        displayOptions: { showName: true },
      }));

    // Filter wish sites by selected organizations
    const wishes = wishSites
      .filter((site: any) => 
        site.latitude && 
        site.longitude && 
        selectedWishOrgs.has(site.organisation_id)
      )
      .map((site: any) => ({
        id: `wish-${site.id}`,
        name: site.name,
        address: site.city,
        city: site.city,
        latitude: site.latitude,
        longitude: site.longitude,
        type: 'wish',
        site_type: 'wish',
        category: 'wish',
        orgId: site.organisation_id,
        displayOptions: { showName: true },
      }));

    // Filter opportunity addresses by type AND by selected carrier organizations
    const oppAddresses = opportunityAddresses
      .filter((addr: any) => {
        if (!addr.latitude || !addr.longitude) return false;
        const org = addr.opportunity?.organization;
        if (!org?.id || !selectedCarrierOrgs.has(org.id)) return false;
        if (addr.address_type === 'dc' && showDCs) return true;
        if (addr.address_type === 'standplaats' && showStandplaatsen) return true;
        return false;
      })
      .map((addr: any) => {
        const org = addr.opportunity?.organization;
        const orgLogo = org?.logo_url 
          || org?.favicon_url 
          || (org?.website 
              ? `https://www.google.com/s2/favicons?sz=32&domain=${org.website.replace(/^https?:\/\//, '').split('/')[0]}`
              : null);
        return {
          id: `opp-addr-${addr.id}`,
          name: addr.label || (addr.address_type === 'dc' ? 'DC' : 'Standplaats'),
          address: addr.address,
          city: addr.city,
          latitude: addr.latitude,
          longitude: addr.longitude,
          type: addr.address_type,
          site_type: addr.address_type,
          category: 'opportunity_address',
          orgLogo,
          orgName: org?.name,
          orgId: org?.id,
          displayOptions: { showName: true },
        };
      });

    return [...chargingLocations, ...competitors, ...wishes, ...oppAddresses];
  }, [locations, visibleLocationIds, competitorSites, selectedCompetitorOrgs, wishSites, selectedWishOrgs, opportunityAddresses, showDCs, showStandplaatsen, selectedCarrierOrgs]);

  const getMarkerColor = (site: any) => {
    if (site.category === 'competitor') return '#e53935';
    if (site.category === 'wish') return '#22c55e';
    if (site.category === 'opportunity_address') {
      return site.site_type === 'dc' ? '#9333ea' : '#3b82f6';
    }
    const stageGroup = getStageGroup(site.type);
    const stage = STAGES.find((s) => s.id === stageGroup);
    return stage?.color || '#666';
  };

  const handleExport = async () => {
    if (!mapRef.current) return;
    setExporting(true);
    try {
      const canvas = await html2canvas(mapRef.current, {
        useCORS: true,
        scale: 2,
        backgroundColor: '#ffffff',
      });
      const link = document.createElement('a');
      link.download = `presentatie-${new Date().toISOString().split('T')[0]}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      toast({ title: 'Export succesvol' });
    } catch {
      toast({ title: 'Export mislukt', variant: 'destructive' });
    } finally {
      setExporting(false);
    }
  };

  const locationsByStage = useMemo(() => {
    return STAGES.reduce((acc, stage) => {
      acc[stage.id] = locations.filter((loc: any) => getStageGroup(loc.stage) === stage.id);
      return acc;
    }, {} as Record<string, any[]>);
  }, [locations]);

  // Toggle all competitor orgs
  const toggleAllCompetitors = (checked: boolean) => {
    if (checked) {
      setSelectedCompetitorOrgs(new Set(competitorOrgs.map((o: any) => o.id)));
    } else {
      setSelectedCompetitorOrgs(new Set());
    }
  };

  // Toggle all wish orgs
  const toggleAllWishes = (checked: boolean) => {
    if (checked) {
      setSelectedWishOrgs(new Set(wishOrgs.map((o: any) => o.id)));
    } else {
      setSelectedWishOrgs(new Set());
    }
  };

  // Toggle all carrier orgs
  const toggleAllCarriers = (checked: boolean) => {
    if (checked) {
      setSelectedCarrierOrgs(new Set(opportunityOrgs.map((o: any) => o.id)));
    } else {
      setSelectedCarrierOrgs(new Set());
    }
  };

  // Auto-select all carriers when DC or Standplaatsen is first enabled
  useEffect(() => {
    if ((showDCs || showStandplaatsen) && !carrierSelectionInitialized && opportunityOrgs.length > 0) {
      setSelectedCarrierOrgs(new Set(opportunityOrgs.map((o: any) => o.id)));
      setCarrierSelectionInitialized(true);
    }
    // Reset when both are off
    if (!showDCs && !showStandplaatsen) {
      setCarrierSelectionInitialized(false);
    }
  }, [showDCs, showStandplaatsen, opportunityOrgs, carrierSelectionInitialized]);

  const isCustomBranding = selectedBranding === 'custom' && selectedOrgId;

  return (
    <div className="fixed inset-0 bg-background z-40 flex flex-col overflow-hidden">
      {/* Professional Header */}
      <header 
        className="flex items-center justify-between px-4 py-3 border-b bg-white shrink-0"
        style={{ 
          borderBottomColor: currentBranding.accentColor,
          borderBottomWidth: '3px',
        }}
      >
        <div className="flex items-center gap-4">
          {isCustomBranding && currentBranding.logo ? (
            <>
              <img src={currentBranding.logo} alt={currentBranding.name} className="h-10 object-contain" />
              <div className="flex flex-col">
                <span className="font-semibold text-foreground">{currentBranding.name}</span>
                <span className="text-xs text-muted-foreground">Laadlocaties Dashboard</span>
              </div>
            </>
          ) : (
            <>
              <img src={gvccLogo} alt="GreenVision Charging" className="h-10 object-contain" />
              <div className="flex flex-col">
                <span className="font-semibold text-foreground">GreenVision Charging</span>
                <span className="text-xs text-muted-foreground">Laadlocaties Dashboard</span>
              </div>
            </>
          )}
        </div>
        
        <div className="flex items-center gap-2">
          {isCustomBranding && (
            <div className="flex items-center gap-2 mr-4 text-xs text-muted-foreground">
              <span>powered by</span>
              <img src={gvccLogo} alt="GreenVision" className="h-5 object-contain opacity-60" />
            </div>
          )}
          
          <Button
            variant="outline"
            size="sm"
            onClick={() => setFiltersPanelOpen(!filtersPanelOpen)}
            className="gap-1"
          >
            <Settings2 className="h-4 w-4" />
            Filters
          </Button>
          <Button
            variant="default"
            size="sm"
            onClick={handleExport}
            disabled={exporting}
            className="gap-1"
            style={{ 
              backgroundColor: currentBranding.accentColor,
            }}
          >
            <Download className="h-4 w-4" />
            {exporting ? 'Exporteren...' : 'Export'}
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
          >
            <X className="h-5 w-5" />
          </Button>
        </div>
      </header>

      {/* Main content - fills remaining space */}
      <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
        {/* Map - takes full remaining height */}
        <div ref={mapRef} className="flex-1 relative min-h-0 overflow-hidden">
          <div className="absolute inset-0">
            <MapComponent
              sites={mapSites}
              getMarkerColor={getMarkerColor}
              portalType="crm"
              markerSize={markerSize}
              maintainZoom={true}
              className="h-full w-full"
            />
          </div>
        </div>

        {/* Filters toggle button */}
        <Button
          variant="secondary"
          size="sm"
          className={cn(
            "absolute left-0 top-1/2 -translate-y-1/2 z-50 rounded-l-none shadow-lg",
            filtersPanelOpen && "hidden"
          )}
          onClick={() => setFiltersPanelOpen(true)}
        >
          <Settings2 className="h-4 w-4 mr-1" />
          <ChevronRight className="h-4 w-4" />
        </Button>

        {/* Filters panel */}
        <div
          className={cn(
            "absolute left-0 top-0 bottom-0 w-full sm:w-80 bg-background/95 backdrop-blur-sm border-r shadow-xl z-50 transition-transform duration-300",
            filtersPanelOpen ? "translate-x-0" : "-translate-x-full"
          )}
        >
          <div className="p-4 border-b flex items-center justify-between">
            <h3 className="font-semibold">Filters</h3>
            <Button variant="ghost" size="icon" onClick={() => setFiltersPanelOpen(false)}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
          </div>

          <div className="p-4 space-y-4 overflow-y-auto max-h-[calc(100vh-180px)]">
            {/* Stage filters */}
            <div className="space-y-2">
              <Label className="font-medium flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-primary" />
                Laadlocaties
              </Label>
              {STAGES.map((stage) => {
                const stageLocations = locationsByStage[stage.id] || [];
                const allChecked = stageLocations.length > 0 && stageLocations.every((loc: any) => visibleLocationIds.has(loc.id));
                
                return (
                  <Label key={stage.id} className="flex items-center gap-2 cursor-pointer pl-4">
                    <Checkbox
                      checked={allChecked}
                      onCheckedChange={(checked) => {
                        const newSet = new Set(visibleLocationIds);
                        stageLocations.forEach((loc: any) => {
                          if (checked) newSet.add(loc.id);
                          else newSet.delete(loc.id);
                        });
                        setVisibleLocationIds(newSet);
                      }}
                    />
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: stage.color }} />
                    <span className="text-sm">{stage.label}</span>
                    <Badge variant="secondary" className="ml-auto text-xs">
                      {stageLocations.length}
                    </Badge>
                  </Label>
                );
              })}
            </div>

            {/* Competitor organizations */}
            <Collapsible open={competitorExpanded} onOpenChange={setCompetitorExpanded}>
              <div className="space-y-2 pt-2 border-t">
                <CollapsibleTrigger className="flex items-center gap-2 w-full">
                  <Label className="font-medium flex items-center gap-2 flex-1 cursor-pointer">
                    <div className="w-2 h-2 rounded-full bg-red-500" />
                    Concurrentie
                    <Badge variant="secondary" className="ml-auto text-xs">
                      {selectedCompetitorOrgs.size}/{competitorOrgs.length}
                    </Badge>
                  </Label>
                  <ChevronDown className={cn("h-4 w-4 transition-transform", competitorExpanded && "rotate-180")} />
                </CollapsibleTrigger>
                <CollapsibleContent className="space-y-1 pl-4">
                  <Label className="flex items-center gap-2 cursor-pointer text-muted-foreground">
                    <Checkbox
                      checked={selectedCompetitorOrgs.size === competitorOrgs.length && competitorOrgs.length > 0}
                      onCheckedChange={toggleAllCompetitors}
                    />
                    <span className="text-xs">Alles selecteren</span>
                  </Label>
                  {competitorOrgs.map((org: any) => (
                    <Label key={org.id} className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={selectedCompetitorOrgs.has(org.id)}
                        onCheckedChange={(checked) => {
                          const newSet = new Set(selectedCompetitorOrgs);
                          if (checked) newSet.add(org.id);
                          else newSet.delete(org.id);
                          setSelectedCompetitorOrgs(newSet);
                        }}
                      />
                      {org.logo_url ? (
                        <img src={org.logo_url} alt="" className="h-4 w-4 object-contain" />
                      ) : (
                        <div className="w-3 h-3 rounded-full bg-red-500" />
                      )}
                      <span className="text-sm truncate">{org.name}</span>
                    </Label>
                  ))}
                </CollapsibleContent>
              </div>
            </Collapsible>

            {/* Wish organizations */}
            <Collapsible open={wishExpanded} onOpenChange={setWishExpanded}>
              <div className="space-y-2 pt-2 border-t">
                <CollapsibleTrigger className="flex items-center gap-2 w-full">
                  <Label className="font-medium flex items-center gap-2 flex-1 cursor-pointer">
                    <div className="w-2 h-2 rounded-full bg-green-500" />
                    Wensen
                    <Badge variant="secondary" className="ml-auto text-xs">
                      {selectedWishOrgs.size}/{wishOrgs.length}
                    </Badge>
                  </Label>
                  <ChevronDown className={cn("h-4 w-4 transition-transform", wishExpanded && "rotate-180")} />
                </CollapsibleTrigger>
                <CollapsibleContent className="space-y-1 pl-4">
                  <Label className="flex items-center gap-2 cursor-pointer text-muted-foreground">
                    <Checkbox
                      checked={selectedWishOrgs.size === wishOrgs.length && wishOrgs.length > 0}
                      onCheckedChange={toggleAllWishes}
                    />
                    <span className="text-xs">Alles selecteren</span>
                  </Label>
                  {wishOrgs.map((org: any) => (
                    <Label key={org.id} className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={selectedWishOrgs.has(org.id)}
                        onCheckedChange={(checked) => {
                          const newSet = new Set(selectedWishOrgs);
                          if (checked) newSet.add(org.id);
                          else newSet.delete(org.id);
                          setSelectedWishOrgs(newSet);
                        }}
                      />
                      {org.logo_url ? (
                        <img src={org.logo_url} alt="" className="h-4 w-4 object-contain" />
                      ) : (
                        <div className="w-3 h-3 rounded-full bg-green-500" />
                      )}
                      <span className="text-sm truncate">{org.name}</span>
                    </Label>
                  ))}
                </CollapsibleContent>
              </div>
            </Collapsible>

            {/* DC's & Standplaatsen with carrier filtering */}
            <Collapsible open={carrierExpanded} onOpenChange={setCarrierExpanded}>
              <div className="space-y-2 pt-2 border-t">
                <CollapsibleTrigger className="flex items-center gap-2 w-full">
                  <Label className="font-medium flex items-center gap-2 flex-1 cursor-pointer">
                    <div className="w-2 h-2 rounded-full bg-purple-500" />
                    Vervoerder Locaties
                    <Badge variant="secondary" className="ml-auto text-xs">
                      {selectedCarrierOrgs.size}/{opportunityOrgs.length}
                    </Badge>
                  </Label>
                  <ChevronDown className={cn("h-4 w-4 transition-transform", carrierExpanded && "rotate-180")} />
                </CollapsibleTrigger>
                
                {/* Type toggles */}
                <div className="space-y-1 pl-4">
                  <Label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox
                      checked={showDCs}
                      onCheckedChange={(checked) => setShowDCs(checked as boolean)}
                    />
                    <div className="w-3 h-3 rounded-full bg-purple-500" />
                    <span className="text-sm">DC's</span>
                  </Label>
                  <Label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox
                      checked={showStandplaatsen}
                      onCheckedChange={(checked) => setShowStandplaatsen(checked as boolean)}
                    />
                    <div className="w-3 h-3 rounded-full bg-blue-500" />
                    <span className="text-sm">Standplaatsen</span>
                  </Label>
                </div>

                {/* Carrier organization filters */}
                <CollapsibleContent className="space-y-1 pl-4 pt-2 border-t mt-2">
                  <Label className="flex items-center gap-2 cursor-pointer text-muted-foreground">
                    <Checkbox
                      checked={selectedCarrierOrgs.size === opportunityOrgs.length && opportunityOrgs.length > 0}
                      onCheckedChange={toggleAllCarriers}
                    />
                    <span className="text-xs">Alle vervoerders</span>
                  </Label>
                  {opportunityOrgs.map((org: any) => {
                    const orgLogo = org.logo_url || org.favicon_url;
                    return (
                      <Label key={org.id} className="flex items-center gap-2 cursor-pointer">
                        <Checkbox
                          checked={selectedCarrierOrgs.has(org.id)}
                          onCheckedChange={(checked) => {
                            const newSet = new Set(selectedCarrierOrgs);
                            if (checked) newSet.add(org.id);
                            else newSet.delete(org.id);
                            setSelectedCarrierOrgs(newSet);
                          }}
                        />
                        {orgLogo ? (
                          <img src={orgLogo} alt="" className="h-4 w-4 object-contain" />
                        ) : (
                          <div className="w-3 h-3 rounded-full bg-purple-500" />
                        )}
                        <span className="text-sm truncate">{org.name}</span>
                      </Label>
                    );
                  })}
                </CollapsibleContent>
              </div>
            </Collapsible>

            {/* Settings */}
            <div className="space-y-3 pt-2 border-t">
              <Label className="font-medium flex items-center gap-2">
                <Settings2 className="h-3 w-3" />
                Instellingen
              </Label>
              
              <div className="space-y-2 pl-4">
                <Label className="text-sm">Marker grootte: {markerSize}px</Label>
                <Slider
                  value={[markerSize]}
                  onValueChange={([val]) => setMarkerSize(val)}
                  min={15}
                  max={50}
                  step={1}
                />
              </div>
            </div>

            {/* Branding section in filters */}
            <Collapsible open={brandingExpanded} onOpenChange={setBrandingExpanded}>
              <div className="space-y-2 pt-2 border-t">
                <CollapsibleTrigger className="flex items-center gap-2 w-full">
                  <Label className="font-medium flex items-center gap-2 flex-1 cursor-pointer">
                    <Palette className="h-3 w-3" />
                    Branding
                  </Label>
                  <ChevronDown className={cn("h-4 w-4 transition-transform", brandingExpanded && "rotate-180")} />
                </CollapsibleTrigger>
                <CollapsibleContent className="space-y-2 pl-4 pt-2">
                  {/* GreenVision option */}
                  <Label 
                    className={cn(
                      "flex items-center gap-2 cursor-pointer p-2 rounded border",
                      selectedBranding === 'greenvision' ? "border-primary bg-primary/5" : "border-transparent"
                    )}
                    onClick={() => { setSelectedBranding('greenvision'); setSelectedOrgId(null); }}
                  >
                    <input 
                      type="radio" 
                      checked={selectedBranding === 'greenvision'} 
                      onChange={() => { setSelectedBranding('greenvision'); setSelectedOrgId(null); }}
                      className="sr-only"
                    />
                    <img src={gvccLogo} className="h-5 object-contain" alt="GreenVision" />
                    <span className="text-sm">GreenVision</span>
                  </Label>
                  
                  {/* Organization options */}
                  {opportunityOrgs.map((org: any) => (
                    <Label 
                      key={org.id} 
                      className={cn(
                        "flex items-center gap-2 cursor-pointer p-2 rounded border",
                        selectedOrgId === org.id ? "border-primary bg-primary/5" : "border-transparent"
                      )}
                      onClick={() => { setSelectedBranding('custom'); setSelectedOrgId(org.id); }}
                    >
                      <input 
                        type="radio" 
                        checked={selectedOrgId === org.id}
                        onChange={() => { setSelectedBranding('custom'); setSelectedOrgId(org.id); }}
                        className="sr-only"
                      />
                      {(org.logo_url || org.favicon_url) ? (
                        <img src={org.logo_url || org.favicon_url} className="h-5 w-5 object-contain" alt="" />
                      ) : (
                        <Building2 className="h-5 w-5 text-muted-foreground" />
                      )}
                      <span className="text-sm truncate">{org.name}</span>
                    </Label>
                  ))}
                </CollapsibleContent>
              </div>
            </Collapsible>
          </div>
        </div>
      </div>
    </div>
  );
}
